import React, { Children, Component } from 'react';

import './card-list.scss';

import { CardListAPI, LayoutGap } from '@philips-emr/tws-components-api';
import { Expression, Size } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isMobile, isValidWidth, propagateProps, trigger } from '@philips-emr/tws-utils';

import { AvatarCard, Link, Loader, NotFound } from '..';
import addIconMobileSvg from '../../../resources/assets/components/plus-circle-sm-dark.svg';
import addIconSvg from '../../../resources/assets/components/plus-simple.svg';

const acceptedTypes = [
  ComponentType.DATA_DISPLAY,
];

function ListItem({ children, className }) {
  const names = classNames({
    'o-card-list__item': true,
    'u-col-4': true,
    'u-col-3@lg': true,
    [className]: Boolean(className),
  });

  return <li className={names}>{children}</li>;
}

ListItem.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  className: string,
};

@AwesomeComponent({ type: ComponentType.LIST, api: CardListAPI })
class CardList extends Component {
  static propTypes = {
    addNewExpression: expression,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: arrayOf(object).notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    loading: bool,
    onAddNewClick: eventListener,
    onClick: eventListener,
    onDoubleClick: eventListener,
    onContextMenuClick: eventListener,
    placeholderCount: number,
    showAddNew: bool,
    showDates: bool,
    uuid: string,
    noWrap: bool,
    style: object.notEnumerable(),
    rowGap: oneOf(enumValues(LayoutGap)).group('layout'),
    columnGap: oneOf(enumValues(LayoutGap)).group('layout'),
  };

  static defaultProps = {
    addNewExpression: Expression.ADD_NEW,
    data: [],
    emptyResultTitleExpression: Expression.RESULTS_NOT_FOUND,
    loading: false,
    placeholderCount: 0,
    showAddNew: true,
    showDates: true,
    style: {},
    noWrap: false,
    rowGap: LayoutGap.LARGE,
    columnGap: LayoutGap.LARGE,
  };

  renderEmptyMessage() {
    const { data, emptyResultSubtitleExpression, emptyResultTitleExpression, loading, showAddNew } = this.props;

    if (data.length || loading || showAddNew) {
      return null;
    }

    return (
      <NotFound
        className="c-card-list__not-found"
        titleExpression={emptyResultTitleExpression}
        subtitleExpression={emptyResultSubtitleExpression}
      />
    );
  }

  renderLoading() {
    const { loading } = this.props;

    if (!loading) {
      return null;
    }

    return (
      <Loader className="c-card-list__loader u-hide@empty" centered size={isMobile() ? 32 : 64} />
    );
  }

  renderAddNew() {
    const { addNewExpression, onAddNewClick, showAddNew, showDates } = this.props;

    if (!showAddNew || isMobile()) {
      return null;
    }

    return (
      <AvatarCard
        width={0}
        className="u-grid-col-4 u-grid-col-3@lg"
        dateVisible={showDates}
        image={addIconSvg}
        onClick={onAddNewClick}
        title={addNewExpression}
      />
    );
  }

  renderPlaceholderCards() {
    const { placeholderCount, children } = this.props;
    const [card = {}] = Children.toArray(children);
    const { width = 3, className } = card.props || {};

    const phClassName = classNames({
      'c-card-list__placeholder': true,
      'u-hide@xsm': true,
      'u-hide@sm': true,
      [`u-grid-col-${width}`]: isValidWidth(width),
      [className]: Boolean(className),
    });

    return Array.from({ length: placeholderCount }).map((_, index) => (
      <div key={`placeholder_${index}`} className={phClassName} />
    ));
  }

  renderCards() {
    const { uuid, children, data, onClick, showDates, onDoubleClick, onContextMenuClick } = this.props;

    if (!Children.count(children)) {
      const Container = ContainerProvider.provide(uuid);

      return (
        <Container className="c-card-list__container u-grid-col-12" uuid={uuid} acceptedTypes={acceptedTypes} repeated />
      );
    }

    return data.map((card, index) => {
      const inheritedProps = {
        ...card,
        dateVisible: showDates,
        identifier: index,
        onClick: trigger(onClick, card),
        onDoubleClick,
        onMenuClick: onContextMenuClick,
      };

      return propagateProps(children, inheritedProps, true);
    });
  }

  getStyle() {
    const { rowGap, columnGap, style } = this.props;

    return {
      '--col-gap': columnGap,
      '--row-gap': rowGap,
      ...style,
    };
  }

  renderCardsContainer() {
    const { className, loading, noWrap, data } = this.props;

    if (!Array.isArray(data) || !data.length && loading) {
      return null;
    }

    const names = classNames({
      'o-grid-card-list': true,
      'o-grid-card-list--no-wrap': noWrap,
      [className]: Boolean(className),
    });

    return (
      <div className={names} style={this.getStyle()}>
        {this.renderAddNew()}
        {this.renderCards()}
        {this.renderPlaceholderCards()}
      </div>
    );
  }

  renderAddNewMobile() {
    const { addNewExpression, loading, onAddNewClick, showAddNew } = this.props;

    if (!isMobile() || loading ||  !showAddNew) {
      return null;
    }

    return (
      <div className="c-card-list__link u-hide@gsm">
        <Link icon={addIconMobileSvg} onClick={onAddNewClick} size={Size.SMALL}>
          {msg(addNewExpression)}
        </Link>
      </div>
    );
  }

  render() {
    const { loading, showDates } = this.props;

    const rootClassName = classNames({
      'c-card-list': true,
      'c-card-list--no-date': !showDates,
      'is-loading': loading,
    });

    return (
      <div className={rootClassName}>
        {this.renderEmptyMessage()}
        {this.renderCardsContainer()}
        {this.renderLoading()}
        {this.renderAddNewMobile()}
      </div>
    );
  }
}

export default CardList;
